/**
 * API shim for the vendored gen-process components.
 *
 * The source components import `RunApi` / `LookupsApi` from `./api`. The
 * standalone app talked to `/run/*`, `/lookups`, `/processes` with a string
 * process id. twasta is project-scoped and file-based, so we re-expose the same
 * object shapes but route them through twasta's `/genprocess/*` endpoints
 * (`@/api/genprocess`). A module-level context carries the current project,
 * since the source components never pass one.
 */

import {
  previewProcess, persistProcess, recalcProcessRow, fetchProcessCriteria,
  fetchLookupsRequired, searchLookup, listLookups, getLookup, saveLookup,
  deleteLookup,
} from '@/api/genprocess'
import type {
  GenProcess, LookupDefinition, PreviewResponse, PersistResponse,
} from '../types'
import type { LookupSummary } from './types'

// Re-export the types the vendored components import from `./api`.
export type { ColumnLookupRow } from '../types'
export type { CriterionItem as CriterionDef } from '../types'

// ── ambient context ─────────────────────────────────────────────────────────
// Set by the designer/preview surface on mount so the project-less source API
// calls (e.g. LookupsApi.list()) know which project's DB to hit.
let _ctx: { project: string } = { project: '' }
export function setGenprocCtx(ctx: { project: string }): void {
  _ctx = { ..._ctx, ...ctx }
}
/** Current project — used by the lookup-config dialog's table/column pickers. */
export function getGenprocProject(): string {
  return _ctx.project
}

type ProcBody = { process?: GenProcess; params?: Record<string, unknown> }

export const RunApi = {
  preview: (
    body: ProcBody & { limit?: number; with_traces?: boolean },
  ): Promise<PreviewResponse> =>
    previewProcess({
      project: _ctx.project, process: body.process, params: body.params,
      limit: body.limit, withTraces: body.with_traces,
    }),

  persist: (
    body: ProcBody & { rows?: Record<string, unknown>[] },
  ): Promise<PersistResponse> =>
    persistProcess({
      project: _ctx.project, process: body.process, params: body.params,
      rows: body.rows,
    }),

  criteria: (
    body: { process?: GenProcess; source_sql?: string },
  ) =>
    fetchProcessCriteria(_ctx.project, {
      process: body.process, sourceSql: body.source_sql,
    }),

  recalc: (
    body: ProcBody & { row: Record<string, unknown>; edited_column: string },
  ) =>
    recalcProcessRow({
      project: _ctx.project, process: body.process, params: body.params,
      row: body.row, editedColumn: body.edited_column,
    }),

  lookupsRequired: (body: ProcBody) =>
    fetchLookupsRequired({
      project: _ctx.project, process: body.process, params: body.params,
    }),

  columnLookup: (
    body: {
      process?: GenProcess
      column?: string
      criterion?: string
      search?: string
      limit?: number
      row?: Record<string, unknown>
    },
  ) =>
    searchLookup({
      project: _ctx.project, process: body.process, column: body.column,
      criterion: body.criterion, search: body.search, limit: body.limit,
      row: body.row,
    }),
}

export const LookupsApi = {
  list: () => listLookups(_ctx.project) as unknown as Promise<LookupSummary[]>,
  get: (name: string) => getLookup(_ctx.project, name) as unknown as Promise<LookupDefinition>,
  save: (def: LookupDefinition) =>
    saveLookup(_ctx.project, def as unknown as Record<string, unknown>) as unknown as Promise<LookupDefinition>,
  remove: (name: string) => deleteLookup(_ctx.project, name),
}
